<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import campanhasReportAPI from 'dashboard/api/campanhasReport';
import { useFilterQuery } from 'dashboard/components-next/filter-bar/useFilterQuery';
import { useAtendimentoSection } from '../atendimento/useAtendimentoSection';
import { ATENDIMENTO_SCHEMA } from '../atendimento/atendimentoFilters';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import CampanhasNumeros from './CampanhasNumeros.vue';
import CampanhasFunil from './CampanhasFunil.vue';
import CampanhasTabela from './CampanhasTabela.vue';
import CampanhasMapa from './CampanhasMapa.vue';
import CampanhasComparar from './CampanhasComparar.vue';
import CampanhaDetalhe from './CampanhaDetalhe.vue';
import {
  campanhasApiFilters,
  detailKey,
  optionsApiFilters,
} from './campanhasFilters';

// A aba Campanhas (REPORTS-03, fase G): a campanha funcionou? Só aparece com a
// flag `outbounds` (G6), e pede as próprias rotas (`campanhas/*`), com o filtro
// da página, que ela lê do endereço. A barra é a do cabeçalho, a mesma de todas
// as abas (fase H): esta aba só dá a ela as opções das pílulas que são dela
// (`options`), e a página as mostra só com ela aberta.
const emit = defineEmits(['options']);

const { t } = useI18n();
const { dateTime } = useAnalyticsFormat();

const { filters, update } = useFilterQuery(ATENDIMENTO_SCHEMA);

// As opções do filtro vêm sem a própria escolha: escolher uma campanha não as
// pede de novo, e a lista não encolhe conforme se escolhe.
const opcoes = useAtendimentoSection(f => campanhasReportAPI.opcoes(f));
const optionsFilters = computed(() => optionsApiFilters(filters.value));
watch(
  () => JSON.stringify(optionsFilters.value),
  key => opcoes.loadIfStale(key, optionsFilters.value),
  { immediate: true }
);

const timezone = computed(() => opcoes.data.value?.period?.timezone);

// Dois lotes para o mesmo segmento têm o mesmo nome: a data do primeiro envio
// separa os dois.
const campaignOptions = computed(() => {
  const campaigns = opcoes.data.value?.campaigns || [];
  const repeated = title =>
    campaigns.filter(campaign => campaign.title === title).length > 1;
  return campaigns.map(campaign => ({
    id: campaign.key,
    name:
      repeated(campaign.title) && campaign.first_sent_at
        ? `${campaign.title} · ${dateTime(campaign.first_sent_at, timezone.value)}`
        : campaign.title || campaign.key,
  }));
});
const byName = list => (list || []).map(({ name }) => ({ id: name, name }));

// As opções das pílulas desta aba, pela chave do esquema, e os agentes de IA
// da resposta, que a página soma aos das outras abas.
watch(
  () => ({
    agents: opcoes.data.value?.agents || [],
    campanha: campaignOptions.value,
    template: byName(opcoes.data.value?.templates),
    segmento: byName(opcoes.data.value?.segments),
  }),
  options => emit('options', options),
  { immediate: true }
);

// A visão geral: os números e o funil do recorte, a tabela por campanha, o mapa
// das respostas e a comparação por template e por segmento. Cada
// seção pede a própria rota, pela chave do filtro: a mesma chave não pede de
// novo, e a falha de uma não apaga as outras.
const apiFilters = computed(() => campanhasApiFilters(filters.value));
const filterKey = computed(() => JSON.stringify(apiFilters.value));

const resumo = useAtendimentoSection(f => campanhasReportAPI.resumo(f));
const tabela = useAtendimentoSection((f, options) =>
  campanhasReportAPI.tabela(f, options)
);
const mapa = useAtendimentoSection(f => campanhasReportAPI.mapa(f));
const comparar = useAtendimentoSection((f, by) =>
  campanhasReportAPI.comparar(f, by)
);
const sort = ref(null);
const page = ref(1);
// template | segment: da comparação, e não da URL.
const compareBy = ref('template');
// Outro filtro é outra lista: ela volta à primeira página.
watch(filterKey, () => {
  page.value = 1;
});

// O detalhe é o filtro `campanha` com exatamente uma chave; com ele aberto, a
// visão geral não pede nada.
const selectedKey = computed(() => detailKey(filters.value));
const selectedCampaign = computed(
  () =>
    (opcoes.data.value?.campaigns || []).find(
      campaign => campaign.key === selectedKey.value
    ) || null
);

const loadOverview = () => {
  if (selectedKey.value) return;
  const f = apiFilters.value;
  resumo.loadIfStale(filterKey.value, f);
  tabela.loadIfStale(
    `${filterKey.value}|${sort.value || ''}|${page.value}`,
    f,
    {
      sort: sort.value,
      page: page.value,
    }
  );
  mapa.loadIfStale(filterKey.value, f);
  comparar.loadIfStale(
    `${filterKey.value}|${compareBy.value}`,
    f,
    compareBy.value
  );
};
watch([filterKey, sort, page, compareBy], loadOverview, { immediate: true });

// O clique na linha põe só a chave dela em `campanha`, e "Voltar" a tira: o
// resto do filtro fica.
const openCampaign = key => update({ campanha: [key] });
const closeCampaign = () => update({ campanha: [] });
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0" data-test-id="campanhas">
    <p
      v-if="opcoes.failed.value"
      data-test-id="campanhas-opcoes-falhou"
      class="mb-0 text-sm text-n-ruby-11"
    >
      {{ t('CAMPANHAS.FILTERS.OPTIONS_FAILED') }}
    </p>
    <CampanhaDetalhe
      v-if="selectedKey"
      :filters="apiFilters"
      :campaign="selectedCampaign"
      @back="closeCampaign"
    />
    <template v-else>
      <CampanhasNumeros
        :report="resumo.data.value"
        :failed="resumo.failed.value"
      />
      <CampanhasFunil
        :report="resumo.data.value"
        :failed="resumo.failed.value"
      />
      <CampanhasTabela
        v-model:sort="sort"
        v-model:page="page"
        :report="tabela.data.value"
        :failed="tabela.failed.value"
        :loading="tabela.loading.value"
        @open="openCampaign"
      />
      <CampanhasMapa :report="mapa.data.value" :failed="mapa.failed.value" />
      <CampanhasComparar
        v-model:by="compareBy"
        :report="comparar.data.value"
        :failed="comparar.failed.value"
      />
    </template>
  </div>
</template>
